import { keysToCamel, keysToSnake } from './caseConvert';

export type AccessState = 'granted' | 'not_owned' | 'revoked' | 'in_review';

/**
 * Which states put content on screen — the mirror of `grantsContent` in
 * `@rayrovisk/entitlements`, which is the authority.
 *
 * It is restated rather than imported because that package reaches Postgres
 * through its own dependencies and has no business in a browser bundle, which
 * is also why `AccessState` itself is restated above it. Kept in the same file
 * as the type so the two travel together: the server's own comment records that
 * the first copy of this predicate to drift was the one that closed the library
 * on a buyer who had only asked a question.
 *
 * `in_review` grants content deliberately. A refund *request* is not a refund.
 */
export function grantsContent(state: AccessState): boolean {
  return state === 'granted' || state === 'in_review';
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(message: string, status: number, code: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

/**
 * A 403 from this service always carries the state behind it. Screens branch on
 * that state (RS-15) — "you never bought this" and "your refund was processed"
 * are different screens — so it is a type of its own rather than a status code
 * every caller has to remember to unpack.
 */
export class AccessDeniedError extends ApiError {
  readonly state: AccessState;

  constructor(state: AccessState) {
    super('Acesso indisponível.', 403, 'access_denied');
    this.name = 'AccessDeniedError';
    this.state = state;
  }
}

type TokenSource = () => Promise<string | null>;

let accessToken: TokenSource = async () => null;

/** Set once by the session provider; the client never touches Supabase itself. */
export function setAccessTokenSource(source: TokenSource): void {
  accessToken = source;
}

export function apiBaseUrl(): string {
  const url = import.meta.env.VITE_API_BASE_URL;
  if (url) {
    return url;
  }
  // vite.config.ts already refuses to build without the variable; this guard
  // covers bundles produced outside that config. Failing loudly beats the
  // silent localhost fallback that broke roteirovisk.com on 2026-08-05.
  if (import.meta.env.PROD) {
    throw new Error('VITE_API_BASE_URL is not set in this production build.');
  }
  return 'http://localhost:3100';
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  /** Public routes — the sign-in link — are the only ones that skip the token. */
  anonymous?: boolean;
}

export async function apiRequest<T = unknown>(
  path: string,
  options: RequestOptions = {}
): Promise<T> {
  const headers: Record<string, string> = {};

  if (!options.anonymous) {
    const token = await accessToken();
    if (!token) {
      // Not sent at all. A request without a token would come back 401 and
      // read like an expired session rather than like no session.
      throw new ApiError('Sessão expirada. Entre de novo.', 401, 'unauthenticated');
    }
    headers.authorization = `Bearer ${token}`;
  }

  if (options.body !== undefined) {
    headers['content-type'] = 'application/json';
  }

  const response = await fetch(`${apiBaseUrl()}${path}`, {
    method: options.method ?? 'GET',
    headers,
    body: options.body === undefined ? undefined : JSON.stringify(keysToSnake(options.body))
  });

  if (response.status === 204) {
    return null as T;
  }

  const payload: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    throw toError(response.status, payload);
  }

  return keysToCamel<T>(payload);
}

export function toError(status: number, payload: unknown): ApiError {
  const body = payload as
    | { state?: AccessState; error?: { code?: string; message?: string } }
    | null;

  if (status === 403 && body?.state) {
    return new AccessDeniedError(body.state);
  }

  // The service writes a sentence meant for the person on the other side.
  // `fetch` writes "Failed to fetch". Only one of them is worth showing.
  return new ApiError(
    body?.error?.message ?? 'Não conseguimos completar essa ação. Tente de novo.',
    status,
    body?.error?.code ?? 'request_failed'
  );
}

/**
 * Bytes rather than JSON, for the one route that serves an image.
 *
 * `<img src>` cannot carry a bearer token, and the avatar route is authorized,
 * so the bytes are fetched here and handed to the tag as an object URL. The
 * caller owns revoking it.
 */
export async function apiBlob(path: string): Promise<Blob> {
  const token = await accessToken();
  if (!token) {
    throw new ApiError('Sessão expirada. Entre de novo.', 401, 'unauthenticated');
  }

  const response = await fetch(`${apiBaseUrl()}${path}`, {
    headers: { authorization: `Bearer ${token}` },
    cache: 'no-store'
  });

  if (!response.ok) {
    throw new ApiError('Imagem indisponível.', response.status, 'avatar_unavailable');
  }

  return response.blob();
}

